// FILE: src/app/core/localdata/core-sqlite-services/sqlite-conexion.service.ts
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { Filesystem, Directory } from '@capacitor/filesystem';

@Injectable({ providedIn: 'root' })
export class SqliteConexionService {
  private dbName = 'jsof_offline';
  private sqlite!: SQLiteConnection;
  private db!: SQLiteDBConnection;
  private isDbReady = false;

  constructor(private platform: Platform) {
    // 🔹 MODIFICACIÓN: Intentamos instanciar SQLiteConnection en constructor
    // Esto ayuda a evitar CapacitorSQLitePlugin: null en Android/MIUI
    try {
      this.sqlite = new SQLiteConnection(CapacitorSQLite);
      console.log('[SQLite] Instancia SQLite creada en constructor');
    } catch (err) {
      console.warn('[SQLite] No se pudo crear instancia en constructor, se hará después de platform.ready', err);
    }
  }

  async initAllSchemas(): Promise<void> {
    await this.platform.ready();

    // 🔹 MODIFICACIÓN: Forzamos creación de SQLiteConnection si no existe
    if (!this.sqlite) {
      this.sqlite = new SQLiteConnection(CapacitorSQLite);
      console.log('[SQLite] Instancia SQLite creada en initAllSchemas');
    }

    if (this.isDbReady) return;

    try {
      const isConn = (await this.sqlite.isConnection(this.dbName, false)).result;
      if (isConn) this.db = await this.sqlite.retrieveConnection(this.dbName, false);
      else this.db = await this.sqlite.createConnection(this.dbName, false, 'no-encryption', 1, false);
      console.log('[SQLite] Abriendo conexión en entorno:', this.platform.platforms());
      await this.db.open();

      const modules = await this.detectModules();
      for (const module of modules) {
        const schemaSql = await this.loadSchema(module);
        const statements = schemaSql
          .split(';')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        for (const stmt of statements) await this.db.execute(stmt);
      }

      this.isDbReady = true;
      console.log('[SQLite] Todas las tablas inicializadas correctamente ✅');
    } catch (err) {
      console.error('[SQLite] Error al inicializar schemas:', err);
      throw err;
    }
  }

  private async detectModules(): Promise<string[]> {
    return ['productos', 'pending'];
  }

  private async loadSchema(module: string): Promise<string> {
    const fileName = module === 'productos' ? 'product-schema.sql' : `${module}-schema.sql`;
    const path = `assets/sqlite/schema/${module}/${fileName}`;
    const response = await fetch(path);
    if (!response.ok)
      throw new Error(`[SQLite] No se pudo cargar el schema SQL (${module})`);
    return await response.text();
  }

  async execute(sql: string, values: any[] = []): Promise<void> {
    if (!this.isDbReady) await this.initAllSchemas();
    if (values.length > 0) await this.db.run(sql, values);
    else await this.db.execute(sql);
  }

  async query(sql: string, values: any[] = []): Promise<any[]> {
    if (!this.isDbReady) await this.initAllSchemas();
    const res = await this.db.query(sql, values);
    return res.values ?? [];
  }

  async closeConnection(): Promise<void> {
    try {
      await this.sqlite.closeConnection(this.dbName, false);
      this.isDbReady = false;
      console.log('[SQLite] Conexión cerrada correctamente.');
    } catch (err) {
      console.warn('[SQLite] Error al cerrar conexión (no crítico):', err);
    }
  }

  // ======================================================
  // ✅ MODIFICACIÓN: Método seguro para obtener conexión
  // Evita null al usar CapacitorSQLitePlugin en Android/MIUI
  async getConnection(): Promise<SQLiteDBConnection | null> {
    try {
      await this.platform.ready();

      if (!this.sqlite) {
        this.sqlite = new SQLiteConnection(CapacitorSQLite);
        console.log('[SQLite] Instancia SQLite creada en getConnection');
      }

      if (this.db && this.isDbReady) {
        console.log('[SQLite] Retornando conexión activa existente');
        return this.db;
      }

      const isConn = (await this.sqlite.isConnection(this.dbName, false)).result;
      if (isConn) this.db = await this.sqlite.retrieveConnection(this.dbName, false);
      else this.db = await this.sqlite.createConnection(this.dbName, false, 'no-encryption', 1, false);

      await this.db.open();
      this.isDbReady = true;
      console.log('[SQLite] Conexión abierta correctamente');

      return this.db;
    } catch (err) {
      console.error('[SQLite] Error obteniendo conexión:', err);
      return null;
    }
  }
  // ======================================================

  async exportDatabase(): Promise<void> {
    try {
      const dbPathResult = await (CapacitorSQLite as any).getNCDatabasePath({
        database: this.dbName,
        folder: 'databases',
      });

      if (!dbPathResult.path)
        throw new Error('No se pudo obtener la ruta física de la DB');

      const file = await Filesystem.readFile({ path: dbPathResult.path });

      const backupDir = 'jsof_backups';
      try {
        await Filesystem.mkdir({
          path: backupDir,
          directory: Directory.ExternalStorage,
          recursive: true,
        });
      } catch (e) {
        // si ya existe, ignorar
      }

      const destPath = `${backupDir}/${this.dbName}`;
      await Filesystem.writeFile({
        path: destPath,
        data: file.data,
        directory: Directory.ExternalStorage,
        recursive: true,
      });

      console.log(`[SQLite] Backup exportado correctamente a /storage/emulated/0/${destPath}`);
      alert(`✅ Backup creado en /storage/emulated/0/${destPath}`);
    } catch (err) {
      console.error('[SQLite] Error al exportar la base de datos:', err);
      alert('❌ Error al crear backup de la base de datos');
    }
  }
}
